import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Download, FileSpreadsheet } from 'lucide-react'
import { spreadsheetDownloadUrl } from '../../lib/chatApi'
import { useAuth } from '../../components/auth-context'
import { useChatStore } from '../../stores/chatStore'
import { SaveArtifactButton } from './SaveArtifactButton'
import type { SpreadsheetPayload } from './SpreadsheetEmbed'
import { VersionPicker } from './VersionPicker'
import { useActiveWorkspace } from '../../lib/workspace'

export function SpreadsheetCard({
  spreadsheet,
  versions,
  isShared,
}: {
  spreadsheet: SpreadsheetPayload
  versions?: SpreadsheetPayload[]
  isShared?: boolean
}) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const session = useAuth()
  const conversationId = useChatStore((s) => s.activeConversationId)
  const { activeWorkspaceKey } = useActiveWorkspace()
  const history = versions ?? [spreadsheet]
  const hasHistory = history.length > 1
  const clickable = !isShared && !!conversationId

  const openPanelAtVersion = (versionNum: number) => {
    if (!clickable || !conversationId) return
    void navigate({
      to: '/assistant/$conversationId',
      params: { conversationId },
      search: (prev) => ({
        ...prev,
        sheet: spreadsheet.artifactId,
        v: versionNum,
      }),
    })
  }

  const markSaved = () => {
    type Row = { artifact_id?: string; saved_by_user?: boolean }
    const patch = (old: Row[] | undefined) =>
      old?.map((row) =>
        row.artifact_id === spreadsheet.artifactId ? { ...row, saved_by_user: true } : row,
      )
    queryClient.setQueriesData<Row[] | undefined>({ queryKey: ['conversationSpreadsheets'] }, patch)
    queryClient.setQueriesData<Row[] | undefined>({ queryKey: ['allSpreadsheets'] }, patch)
    void queryClient.invalidateQueries({ queryKey: ['allSpreadsheets'] })
  }

  return (
    <div data-testid="spreadsheet-embed" className="my-4 w-full max-w-2xl mx-auto">
      <div className="group flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm shadow-slate-200/40">
        <button
          type="button"
          disabled={!clickable}
          onClick={() => openPanelAtVersion(spreadsheet.versionNum)}
          className="flex min-w-0 flex-1 items-center gap-3 rounded-lg px-1 py-1 text-left transition-colors hover:bg-slate-50 disabled:cursor-default disabled:hover:bg-transparent"
          aria-label={`Open ${spreadsheet.title || 'spreadsheet'} in editor`}
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-teal-100 bg-teal-50">
            <FileSpreadsheet size={20} className="text-teal-700" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-gray-900">
              {spreadsheet.title || 'Spreadsheet'}
            </span>
            <span className="block truncate text-xs text-gray-500">Spreadsheet</span>
          </span>
        </button>

        <div className="flex shrink-0 items-center gap-1">
          {hasHistory ? (
            <VersionPicker
              versions={history}
              value={spreadsheet.versionNum}
              onChange={openPanelAtVersion}
              ariaLabel="Open spreadsheet version in editor"
            />
          ) : (
            <span
              className="inline-flex items-center rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[11px] font-medium text-slate-700"
              aria-label={`Version ${spreadsheet.versionNum}`}
            >
              v{spreadsheet.versionNum}
            </span>
          )}
          <a
            href={spreadsheetDownloadUrl(activeWorkspaceKey, spreadsheet.id)}
            download={`${spreadsheet.title || 'spreadsheet'}.xlsx`}
            data-testid="spreadsheet-xlsx-download"
            className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-100"
            aria-label="Download as XLSX"
          >
            <Download size={12} />
            XLSX
          </a>
          {session.authenticated && !isShared && (
            <SaveArtifactButton
              kind="spreadsheet"
              id={spreadsheet.artifactId}
              saved={spreadsheet.savedByUser}
              onSaved={markSaved}
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-100 disabled:cursor-default disabled:border-teal-200 disabled:bg-teal-50 disabled:text-teal-700 disabled:hover:bg-teal-50"
            />
          )}
        </div>
      </div>
    </div>
  )
}
